import { parsePersonalNumber, toNumericValue } from '@staff/shared'
import type {
  Applicant,
  ApplicationDetail,
  ApplicationHousehold,
  UpdateApplicationPayload,
} from '@/services/application'
import {
  EMPLOYMENT_REQUIRES_EMPLOYER,
  type ApplicantForm,
  type BoolString,
  type CreateApplicationFormType,
  type Custody,
  type EmploymentStatus,
  type MaritalStatus,
  type OtherLoanForm,
  type OtherPropertyForm,
  type PropertyType,
  type RequestedProduct,
} from './applicationFormConfig'
import { isRealEstateType } from '@/utils/applicationDisplay'
import { getApplicationForm } from './forms'
import type { Question } from './questions'
import {
  createEmptyApplicant,
  createInitialFormState,
  type CreateApplicationFormState,
} from './composables/applicationFormData'

export const optionalString = (value: string) => {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

const toBoolString = (value: boolean | null | undefined): BoolString | undefined =>
  value === true ? 'true' : value === false ? 'false' : undefined

const fromBoolString = (value: BoolString | undefined): boolean | undefined =>
  value === undefined ? undefined : value === 'true'

const setApplicantForm = (target: CreateApplicationFormState['coApplicants'][number], applicant: Applicant) => {
  const employmentInfo = applicant.employmentInfo

  target.personalNumber = String(applicant.personalNumber)
  target.userId = applicant.userId
  target.email = applicant.email ?? ''
  target.phoneNumber = applicant.phoneNumber ?? ''
  target.maritalStatus = applicant.maritalStatus as MaritalStatus | undefined
  if (employmentInfo) {
    target.employmentStatus = employmentInfo.employmentStatus as EmploymentStatus | undefined
    target.employer = employmentInfo.employer ?? ''
    target.employerOrganisationNumber = employmentInfo.employerOrganisationNumber ?? ''
    target.employedStartDate = employmentInfo.employedStartDate ?? ''
    target.temporaryEmployedEndDate = employmentInfo.temporaryEmployedEndDate ?? ''
  }
  target.monthlyIncome = applicant.monthlyIncome
  target.monthlyIncomeCurrency = 'SEK'
}

/**
 * Försäkringssvaret, i den form bostadstypen tillåter: en bostadsrätt frågas bara om den är
 * försäkrad och hos vilket bolag, så omfattningsfälten utelämnas där.
 *
 * Allt eller inget. API:t avvisar ett halvt svar, och ett utkast ska gå att spara mitt i
 * ifyllnaden — därför utelämnas hela blocket tills det är komplett för bostadstypen, på samma
 * sätt som createOtherPropertyPayload gör. Vid inskick läses ett utelämnat svar som obesvarat
 * och kompletteringen öppnas, vilket är rätt utfall för en halv ifyllnad.
 */
const createInsurancePayload = (form: CreateApplicationFormState) => {
  const isInsured = fromBoolString(form.isInsured)
  if (isInsured === undefined) return undefined
  if (!isInsured) return { isInsured }

  const insuranceCompany = optionalString(form.insuranceCompany)
  if (!insuranceCompany) return undefined

  const insuranceCompanyOtherName = optionalString(form.insuranceCompanyOtherName)
  if (insuranceCompany === 'OTHER' && !insuranceCompanyOtherName) return undefined

  const asksAboutCover = isRealEstateType(form.collateralType)
  const insurer = {
    isInsured,
    insuranceCompany,
    ...(insuranceCompany === 'OTHER' ? { insuranceCompanyOtherName } : {}),
  }

  if (!asksAboutCover) return insurer

  const isFullyInsured = fromBoolString(form.isFullyInsured)
  if (isFullyInsured === undefined) return undefined
  if (isFullyInsured) return { ...insurer, isFullyInsured }

  if (toNumericValue(form.coverageAmount) === undefined || toNumericValue(form.excessAmount) === undefined) {
    return undefined
  }

  return { ...insurer, isFullyInsured, coverageAmount: form.coverageAmount, excessAmount: form.excessAmount }
}

const createCollateralPayload = (form: CreateApplicationFormState) => {
  if (!form.collateralType) return undefined

  const balcony = fromBoolString(form.balcony)
  const elevator = fromBoolString(form.elevator)
  const insurance = createInsurancePayload(form)
  const street = optionalString(form.street)
  const streetNo = optionalString(form.streetNo)
  const city = optionalString(form.city)
  const apartmentNbr = optionalString(form.apartmentNbr)
  const apartmentNbrBRF = optionalString(form.apartmentNbrBRF)
  const apartmentDetails = apartmentNbr
    ? {
        hasApartmentNbr: true,
        apartmentNbr,
      }
    : {
        hasApartmentNbr: false,
        ...(toNumericValue(form.rooms) !== undefined ? { rooms: String(form.rooms) } : {}),
        ...(toNumericValue(form.floors) !== undefined ? { floors: form.floors } : {}),
        ...(toNumericValue(form.nbrOfFloors) !== undefined ? { nbrOfFloors: form.nbrOfFloors } : {}),
        ...(balcony !== undefined ? { balcony } : {}),
        ...(elevator !== undefined ? { elevator } : {}),
        ...(toNumericValue(form.size) !== undefined ? { size: form.size } : {}),
      }

  return {
    type: form.collateralType,
    ...(toNumericValue(form.monthlyFee) !== undefined ? { monthlyFee: form.monthlyFee } : {}),
    ...(street ? { street } : {}),
    ...(streetNo ? { streetNo } : {}),
    hasStreetNo: Boolean(streetNo),
    ...(city ? { city } : {}),
    ...(toNumericValue(form.zip) !== undefined ? { zip: form.zip } : {}),
    ...(insurance ? { insurance } : {}),
    ...(form.collateralType === 'apartment'
      ? {
          ...(apartmentNbrBRF ? { apartmentNbrBRF } : {}),
          ...apartmentDetails,
        }
      : {}),
    ...((form.collateralType === 'house' || form.collateralType === 'vacation') &&
    toNumericValue(form.size) !== undefined
      ? { size: form.size }
      : {}),
  }
}

export const hydrateHouseholdIntoForm = (
  form: CreateApplicationFormState,
  household: ApplicationHousehold | undefined | null,
) => {
  if (!household) return

  form.children.splice(0)
  for (const child of household.children ?? []) {
    form.children.push({
      age: child.age,
      custody: String(child.custody) as Custody,
      maintenanceSupportIncome: child.maintenanceSupportIncome ? 'true' : 'false',
      maintenanceSupportCost: child.maintenanceSupportCost ? 'true' : 'false',
    })
  }
  form.cars.splice(0)
  for (const car of household.cars ?? []) {
    form.cars.push({
      ownershipType: car.ownershipType,
      monthlyCost: car.monthlyCost,
    })
  }
  form.capitalAssets.splice(0)
  for (const asset of household.capitalAssets ?? []) {
    form.capitalAssets.push({
      type: asset.type,
      amount: asset.amount,
    })
  }
  form.otherLoans.splice(0)
  for (const loan of household.otherLoans ?? []) {
    form.otherLoans.push({
      type: loan.type as OtherLoanForm['type'],
      amount: loan.amount,
    })
  }
  form.otherProperties.splice(0)
  for (const property of household.otherProperties ?? []) {
    form.otherProperties.push({
      type: property.type,
      keepProperty: toBoolString(property.keepProperty),
      loanAmount: property.loanAmount,
      monthlyFee: property.monthlyFee,
      ownershipShare: property.ownershipShare,
      taxValue: property.taxValue,
      isPropertyOld: toBoolString(property.isPropertyOld),
    })
  }
}

export const hydrateApplicantsIntoForm = (form: CreateApplicationFormState, applicants: Applicant[]) => {
  const mainApplicant = applicants.find((applicant) => applicant.roles?.isMainApplicant) ?? applicants[0]

  if (mainApplicant) {
    setApplicantForm(form, mainApplicant)
  }

  form.coApplicants.splice(0)
  applicants
    .filter((applicant) => applicant !== mainApplicant)
    .forEach((applicant) => {
      const coApplicant = createEmptyApplicant()
      setApplicantForm(coApplicant, applicant)
      form.coApplicants.push(coApplicant)
    })
}

export const hydrateApplicationForm = (form: CreateApplicationFormState, application: ApplicationDetail) => {
  Object.assign(form, createInitialFormState())

  form.type = application.type as CreateApplicationFormType
  form.debtId = application.debtId ?? ''
  form.comment = application.comment ?? ''
  form.brokerExternalId = application.brokerExternalId ?? ''
  form.amountAppliedFor = application.amountAppliedFor
  form.propertyEstimation = application.propertyEstimation
  form.cashContribution = application.statedCashContribution
  form.propertyTypeEstimation = application.propertyTypeEstimation as PropertyType | undefined
  form.sizeEstimation = application.sizeEstimation
  form.monthlyFeeEstimation = application.monthlyFeeEstimation
  form.kommunEstimation = application.kommunEstimation ?? ''
  form.topupPurpose = application.topupPurpose
  form.topupPurposeOther = application.topupPurposeOther ?? ''
  form.isHighPrio = toBoolString(application.isHighPrio)
  form.isNewProduction = toBoolString(application.isNewProduction) ?? 'false'
  if (application.requestedFeatures) {
    form.requestedFeatures.splice(
      0,
      form.requestedFeatures.length,
      ...(application.requestedFeatures as RequestedProduct[]),
    )
  }

  hydrateApplicantsIntoForm(form, application.applicants ?? [])

  hydrateHouseholdIntoForm(form, application.household)

  const collateral = application.collateralProperty
  if (collateral) {
    form.collateralType = collateral.type
    form.monthlyFee = collateral.monthlyFee
    form.street = collateral.street
    form.streetNo = collateral.streetNo ?? ''
    form.city = collateral.city
    form.zip = collateral.zip

    if (collateral.type === 'apartment') {
      form.apartmentNbr = 'apartmentNbr' in collateral ? collateral.apartmentNbr : ''
      form.apartmentNbrBRF = collateral.apartmentNbrBRF ?? ''
      form.rooms = collateral.rooms
      form.floors = collateral.floors
      form.nbrOfFloors = collateral.nbrOfFloors
      form.balcony = toBoolString(collateral.balcony)
      form.elevator = toBoolString(collateral.elevator)
      form.size = collateral.size
    } else {
      form.size = collateral.size
    }

    if (collateral.insurance) {
      const insurance = collateral.insurance

      form.isInsured = toBoolString(insurance.isInsured)
      form.insuranceCompany = insurance.insuranceCompany ?? ''
      form.insuranceCompanyOtherName = insurance.insuranceCompanyOtherName ?? ''
      form.isFullyInsured = toBoolString(insurance.isFullyInsured)
      form.coverageAmount = insurance.coverageAmount
      form.excessAmount = insurance.excessAmount
    }
  }
}

export const buildSharedUpdatePayload = (form: CreateApplicationFormState): UpdateApplicationPayload => {
  const createEmploymentInfoPayload = (applicant: {
    employmentStatus: EmploymentStatus | undefined
    employer: string
    employerOrganisationNumber: string
    employedStartDate: string
    temporaryEmployedEndDate: string
  }) => {
    if (!applicant.employmentStatus) return undefined

    const employer = optionalString(applicant.employer)
    const employerOrganisationNumber = optionalString(applicant.employerOrganisationNumber)
    const employedStartDate = optionalString(applicant.employedStartDate)
    const temporaryEmployedEndDate = optionalString(applicant.temporaryEmployedEndDate)

    return {
      employmentStatus: applicant.employmentStatus,
      ...(EMPLOYMENT_REQUIRES_EMPLOYER.includes(applicant.employmentStatus) && employer ? { employer } : {}),
      ...((applicant.employmentStatus === 'employed' || applicant.employmentStatus === 'hourly') && employedStartDate
        ? { employedStartDate }
        : {}),
      ...(applicant.employmentStatus === 'trialEmployed' && temporaryEmployedEndDate
        ? { temporaryEmployedEndDate }
        : {}),
      ...(applicant.employmentStatus === 'selfEmployed' && employerOrganisationNumber
        ? { employerOrganisationNumber }
        : {}),
    }
  }

  const createApplicantPayload = (applicant: ApplicantForm & { isMainApplicant: boolean }) => {
    const personalNumber = parsePersonalNumber(applicant.personalNumber)
    if (!personalNumber) return undefined

    const employmentInfo = createEmploymentInfoPayload(applicant)
    const email = optionalString(applicant.email)
    const phoneNumber = optionalString(applicant.phoneNumber)

    // E-post och telefon sparas på kundposten, inte på ansökan - skickas de med skrivs
    // kundens uppgifter över i alla hennes engagemang. På en höjning kommer låntagarna från
    // bolånet och fälten är read-only i formuläret, så vi utelämnar dem helt (backendens
    // uppdatering är partiell - utelämnade fält lämnas orörda). PEP frågas inte på ansökan
    // utan anges i KYC-svaren.
    const customerDetails =
      form.type === 'topupLoanApplication'
        ? {}
        : {
            ...(email ? { email } : {}),
            ...(phoneNumber ? { phoneNumber } : {}),
          }

    return {
      personalNumber: Number(personalNumber),
      isMainApplicant: applicant.isMainApplicant,
      ...customerDetails,
      ...(applicant.maritalStatus ? { maritalStatus: applicant.maritalStatus } : {}),
      ...(toNumericValue(applicant.monthlyIncome) !== undefined ? { monthlyIncome: applicant.monthlyIncome } : {}),
      monthlyIncomeCurrency: 'SEK',
      ...(employmentInfo ? { employmentInfo } : {}),
    }
  }

  const createOtherPropertyPayload = (property: OtherPropertyForm) => {
    const keepProperty = fromBoolString(property.keepProperty)
    if (!property.type || keepProperty === undefined) return undefined
    if (!keepProperty) return { type: property.type, keepProperty }
    if (property.type === 'rental') {
      if (toNumericValue(property.monthlyFee) === undefined) return undefined
      return { type: property.type, keepProperty, monthlyFee: property.monthlyFee }
    }
    if (
      toNumericValue(property.loanAmount) === undefined ||
      toNumericValue(property.monthlyFee) === undefined ||
      toNumericValue(property.ownershipShare) === undefined
    ) {
      return undefined
    }
    return {
      type: property.type,
      keepProperty,
      loanAmount: property.loanAmount,
      monthlyFee: property.monthlyFee,
      ownershipShare: property.ownershipShare,
      ...(toNumericValue(property.taxValue) !== undefined ? { taxValue: property.taxValue } : {}),
      ...(property.isPropertyOld ? { isPropertyOld: property.isPropertyOld === 'true' } : {}),
    }
  }

  const createHouseholdPayload = (applicantPersonalNumbers: number[]) => {
    const cars = form.cars.map((car) => ({
      ownershipType: car.ownershipType,
      monthlyCost: car.monthlyCost,
    }))
    const capitalAssets = form.capitalAssets.map((asset) => ({
      type: asset.type,
      amount: asset.amount,
    }))
    const otherLoans = form.otherLoans.map((loan) => ({
      type: loan.type,
      amount: loan.amount,
    }))
    const otherProperties = form.otherProperties
      .map(createOtherPropertyPayload)
      .filter((property): property is NonNullable<typeof property> => Boolean(property))
    const household = {
      applicants: applicantPersonalNumbers,
      children: form.children.map((child) => ({
        age: child.age,
        custody: Number(child.custody),
        maintenanceSupportIncome: child.maintenanceSupportIncome === 'true',
        maintenanceSupportCost: child.maintenanceSupportCost === 'true',
      })),
      cars,
      capitalAssets,
      otherLoans,
      otherProperties,
    }

    return household.applicants.length ||
      household.children.length ||
      cars.length ||
      capitalAssets.length ||
      otherLoans.length ||
      otherProperties.length
      ? household
      : undefined
  }

  const mainApplicant = createApplicantPayload({ ...form, isMainApplicant: true })
  const deletedApplicants = form.deletedApplicantPersonalNumbers
    .map((personalNumber) => parsePersonalNumber(personalNumber))
    .filter((personalNumber): personalNumber is string => Boolean(personalNumber))
    .map((personalNumber) => ({ delete: true, personalNumber: Number(personalNumber) }))
  const coApplicants = form.coApplicants
    .map((applicant) => createApplicantPayload({ ...applicant, isMainApplicant: false }))
    .filter((applicant): applicant is NonNullable<typeof applicant> => Boolean(applicant))
  const applicants = [mainApplicant, ...coApplicants].filter((applicant): applicant is NonNullable<typeof applicant> =>
    Boolean(applicant),
  )
  const applicantUpdates = [...deletedApplicants, ...applicants]
  const household = createHouseholdPayload(applicants.map((applicant) => applicant.personalNumber))
  const brokerExternalId = optionalString(form.brokerExternalId)
  const updatePayload: UpdateApplicationPayload = {
    type: form.type,
    ...(brokerExternalId ? { brokerExternalId } : {}),
    ...(applicantUpdates.length ? { applicants: applicantUpdates } : {}),
    ...(household ? { household } : {}),
    comment: form.comment.trim() || null,
  }

  return updatePayload
}

export const buildUpdatePayload = (form: CreateApplicationFormState): UpdateApplicationPayload => {
  const applicationForm = getApplicationForm(form.type)
  const formQuestions = new Set(applicationForm.sections.flatMap((section) => section.questions))
  const hasQuestion = (question: Question) => formQuestions.has(question)
  const updatePayload: UpdateApplicationPayload = {
    ...buildSharedUpdatePayload(form),
    ...(form.isHighPrio ? { isHighPrio: form.isHighPrio === 'true' } : {}),
    ...(toNumericValue(form.amountAppliedFor) !== undefined
      ? { amountAppliedFor: { amount: form.amountAppliedFor, currency: 'SEK' } }
      : { amountAppliedFor: null }),
  }

  if (hasQuestion('CollateralType')) {
    const collateralPayload = createCollateralPayload(form)
    if (collateralPayload) updatePayload.collateralProperty = collateralPayload
  }

  Object.assign(updatePayload, {
    ...(toNumericValue(form.propertyEstimation) !== undefined
      ? { propertyEstimation: form.propertyEstimation }
      : { propertyEstimation: null }),
  })

  if (form.type === 'newLoanApplication') {
    Object.assign(updatePayload, {
      isNewProduction: fromBoolString(form.isNewProduction) ?? false,
      requestedFeatures: form.requestedFeatures,
    })
  }

  if (hasQuestion('TopupPurpose')) {
    const topupPurposeOther = optionalString(form.topupPurposeOther)
    Object.assign(updatePayload, {
      ...(form.topupPurpose ? { topupPurpose: form.topupPurpose } : {}),
      ...(form.topupPurpose === 'other' && topupPurposeOther ? { topupPurposeOther } : {}),
    })
  }

  return updatePayload
}
